import { useEffect, useState, useMemo } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { ROLES, SxStyle } from '../../../types';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import NewsHideSelect from './components/news-hide-select';
import NewsHideCommentSelect from './components/news-hide-comment-select';
import { SUPPORTED_LEAGUES } from '../../../types';
import { useRouteMatch } from 'react-router-dom';
import Header from './components/header';
import NewsPushNotificationColumn from './components/news-push-notification-column';
import ScenePage from '../../common/scene/scene-page';
import { useUserAuth } from '../../../contexts/auth';
import { useNewsArticles } from './hooks/useNews';
import { NewsArticle } from './types';
import moment from 'moment';

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 80px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

export default function NewsList() {
  const { strings } = useStringsContext();
  const { params } = useRouteMatch<{ abbrv?: string }>();
  const selectedAbbrv = params.abbrv || null;
  const { fetchNewsArticles /* , updateNewsArticle */ } = useNewsArticles();
  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const isReadOnly = !isSuperAdmin && hasTeamRole(selectedAbbrv || '', ROLES.VIEWER);
  const [newsDocs, setNewsDocs] = useState<NewsArticle[] | null>(null);
  const isLeague = selectedAbbrv ? selectedAbbrv in SUPPORTED_LEAGUES : false;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getNews = async (selectedAbbrv: string) => {
      setIsLoading(true);
      const data = await fetchNewsArticles(selectedAbbrv);
      setNewsDocs(data);
      setIsLoading(false);
    };

    if (selectedAbbrv) {
      getNews(selectedAbbrv);
    }
  }, [selectedAbbrv, isLeague]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'dateTime', headerName: strings.news.fieldNameDate, width: 160 },
      { field: 'title', headerName: strings.news.fieldNameTitle, width: 400 },
      {
        field: 'description',
        headerName: strings.news.fieldNameDescription,
        width: 400,
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'comments',
        headerName: strings.news.fieldNameCommentsShow,
        maxWidth: 150,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <NewsHideCommentSelect
            newsItem={params.row}
            selectedAbbrv={selectedAbbrv!}
            isLeague={isLeague}
            isReadOnly={isReadOnly}
          />
        ),
      },
      {
        field: 'hidden',
        headerName: strings.news.fieldNameNewsShow,
        minWidth: 100,
        maxWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
          <NewsHideSelect
            newsItem={params.row}
            selectedAbbrv={selectedAbbrv!}
            isLeague={isLeague}
            isReadOnly={isReadOnly}
          />
        ),
      },
      {
        field: 'push-notification',
        headerName: strings.news.pushNotificationColumn,
        minWidth: 270,
        renderCell: (params: GridRenderCellParams) => (
          <NewsPushNotificationColumn
            key={params.row.id}
            newsItem={params.row}
            selectedAbbrv={selectedAbbrv!}
            isLeague={isLeague}
          />
        ),
      },
    ],
    [selectedAbbrv, strings, isLeague]
  );

  const rowsSent = (newsDocs || []).map((newsArticle) => {
    return {
      ...newsArticle,
      dateTime: moment(newsArticle.date).format('YYYY-MM-DD HH:mm'),
    };
  });

  return (
    <ScenePage title={strings.appBar.news}>
      {selectedAbbrv ? (
        <Header teamAbbrv={selectedAbbrv} key={selectedAbbrv} isReadOnly={isReadOnly} />
      ) : null}
      <Paper elevation={2} sx={styles.container}>
        <DataGrid
          sx={styles.dataGrid}
          rows={rowsSent || []}
          columns={columns}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          loading={isLoading}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'timestamp',
                  sort: 'desc',
                },
              ],
            },
          }}
        />
      </Paper>
    </ScenePage>
  );
}
